/**
 * FloatingTopBar — floating pill navigation bar for mobile and tablet viewports.
 *
 * Purpose: Replaces the desktop sidebar on screens narrower than 1024px (lg breakpoint).
 * Flow: fixed pill header → brand centered absolutely → hamburger at inline-start
 *       (toggles MobileNavPopup, icon morphs Menu ↔ X) → bell at inline-end (opens
 *       NotificationCenter popover).
 *
 * Design: 52px pill (3.25rem), 18px radius, frosted translucent fill. The pill is
 *          wrapped in a full-bleed fixed strip whose blurred backdrop covers the
 *          pill plus the equal top/start/end gaps (16px → 24px at sm, matching the
 *          page padding) and fades out below — so content scrolling under the bar
 *          is blurred on all three exposed sides, as one component.
 * RTL-aware via logical `start-*` / `end-*` and Tailwind `rtl:` variants.
 */
import { useState } from "react";
import { Bell, Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/utils";
import { Logo } from "./Logo";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
// Anchor isn't re-exported by the read-only ui wrapper; pull it from the same
// Radix package so the popover can drop from the pill's edges (see render).
import { Anchor as PopoverAnchor } from "@radix-ui/react-popover";
import { NotificationCenter } from "@/features/notifications/components/NotificationCenter";

interface FloatingTopBarProps {
  orgSubtitle?: string;
  onMenuClick: () => void;
  unreadCount?: number;
  menuOpen?: boolean;
  /** Where the "view all" link in the popover should navigate. */
  notificationsHref: string;
}

export function FloatingTopBar({
  orgSubtitle,
  onMenuClick,
  unreadCount = 0,
  menuOpen = false,
  notificationsHref,
}: FloatingTopBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bellOpen, setBellOpen] = useState(false);

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-[var(--z-chrome)] lg:hidden",
        // Equal gap on the three exposed sides (top + start + end) — mirrors the
        // page content padding (p-4 → sm:p-6). The larger bottom padding gives the
        // frosted backdrop a fade tail below the pill.
        "px-4 pt-4 pb-7 sm:px-6 sm:pt-6 sm:pb-9",
        // Only the pill is interactive; the blurred gaps pass clicks through.
        "pointer-events-none",
      )}
      aria-label={t("sidebar.mobileNav")}
    >
      {/* Frosted backdrop — blurs everything scrolling under the whole strip
          (top gap + both side gaps + behind the pill), then fades out at the
          bottom so it melts into the page. Visual only. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          background: "linear-gradient(to bottom, var(--admin-bg) 35%, transparent)",
          maskImage: "linear-gradient(to bottom, black 62%, transparent)",
          WebkitMaskImage: "linear-gradient(to bottom, black 62%, transparent)",
        }}
      />

      {/* Pill — frosted translucent fill above the backdrop, click-enabled.
          The Popover is anchored to the whole pill (not just the bell) so the
          notification dropdown falls from the pill's bottom/end edges — giving it
          the same gap + side inset as the MobileNavPopup on the start side. */}
      <Popover open={bellOpen} onOpenChange={setBellOpen}>
      <PopoverAnchor asChild>
      <div
        className={cn(
          "relative pointer-events-auto h-[3.25rem] rounded-[1.125rem]",
          "flex items-center px-2.5 gap-2",
          "backdrop-blur-xl",
          "shadow-[0_2px_16px_rgba(26,25,23,0.09),0_1px_3px_rgba(26,25,23,0.04)]",
        )}
        style={{
          background: "color-mix(in oklab, var(--card) 82%, transparent)",
          border: "1px solid var(--border)",
        }}
      >
      {/* Hamburger ↔ X — inline-start */}
      <button
        type="button"
        className={cn(
          "relative w-9 h-9 rounded-[0.625rem] flex items-center justify-center shrink-0 overflow-hidden",
          "transition-colors cursor-pointer",
          // Transparent resting fill (both themes); hover reveals the chip.
          "bg-transparent",
          "hover:bg-[var(--sidebar-active-bg)] hover:text-[var(--admin-text)]",
        )}
        style={{ color: "var(--admin-text-muted)" }}
        onClick={(e) => {
          e.stopPropagation();
          onMenuClick();
        }}
        aria-label={menuOpen ? t("sidebar.closeMenu") : t("sidebar.openMenu")}
        aria-expanded={menuOpen}
      >
        <span
          className="absolute inset-0 flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.68,-0.55,0.27,1.55)]"
          style={{
            opacity: menuOpen ? 0 : 1,
            transform: menuOpen ? "rotate(90deg) scale(0.5)" : "rotate(0) scale(1)",
          }}
          aria-hidden
        >
          <Menu className="w-[1.125rem] h-[1.125rem]" strokeWidth={1.75} />
        </span>
        <span
          className="absolute inset-0 flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.68,-0.55,0.27,1.55)]"
          style={{
            opacity: menuOpen ? 1 : 0,
            transform: menuOpen ? "rotate(0) scale(1)" : "rotate(-90deg) scale(0.5)",
          }}
          aria-hidden
        >
          <X className="w-[1.125rem] h-[1.125rem]" strokeWidth={1.75} />
        </span>
      </button>

      {/* Brand — absolutely centered, pointer-events disabled so it never blocks the buttons */}
      <div className="absolute start-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rtl:translate-x-1/2 flex items-center gap-2 pointer-events-none max-w-[58%]">
        {/* Logo is the only interactive part of the (otherwise click-through)
            brand block — taps it to land on the domain home. */}
        <Link
          to="/"
          className="pointer-events-auto shrink-0"
          aria-label={t("sidebar.home", { defaultValue: "Home" })}
        >
          <Logo height={24} />
        </Link>
        {orgSubtitle && (
          <div
            className="text-[8.5px] font-bold uppercase tracking-[0.12em] truncate"
            style={{ color: "var(--admin-text-muted)" }}
          >
            {orgSubtitle}
          </div>
        )}
      </div>

      {/* Bell — inline-end, toggles the notification popover (anchored to pill) */}
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "relative w-9 h-9 rounded-[0.625rem] flex items-center justify-center shrink-0 ms-auto",
            "transition-colors cursor-pointer",
            // Transparent resting fill (both themes); hover reveals the chip.
            "bg-transparent",
            "hover:bg-[var(--sidebar-active-bg)] hover:text-[var(--admin-text)]",
          )}
          style={{ color: "var(--admin-text-muted)" }}
          aria-label={t("sidebar.notifications")}
        >
          <Bell className="w-4 h-4" strokeWidth={1.75} />
          {unreadCount > 0 && (
            <span
              className="absolute top-[0.3125rem] end-[0.3125rem] w-[0.4375rem] h-[0.4375rem] rounded-full"
              style={{
                background: "var(--sidebar-danger)",
                border: "1.5px solid var(--card)",
              }}
              aria-hidden
            />
          )}
        </button>
      </PopoverTrigger>
      </div>
      </PopoverAnchor>
      {/* sideOffset 16 + align end → top = pill bottom + 1rem, end edge = pill
          end edge: same drop + inset as the MobileNavPopup on the start side. */}
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={16}
        className={cn(
          "p-0 overflow-hidden",
          // Same motion as the MobileNavPopup sidebar drop: fade + scale-95 +
          // 8px slide from the top, 200ms ease-out. Radix data-state drives both
          // the enter and (unmount-blocking) exit so it mirrors the sidebar exactly.
          "duration-200 ease-out",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2",
        )}
        style={{ width: 340 }}
      >
        <NotificationCenter
          onNavigate={() => {
            setBellOpen(false);
            navigate(notificationsHref);
          }}
        />
      </PopoverContent>
      </Popover>
    </header>
  );
}
